import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "relative text-primary-dark" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "sm:flex sm:items-center pb-4" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "sm:flex-auto" }, [
    /*#__PURE__*/_createElementVNode("h1", { class: "text-base font-semibold text-primary-dark m-0" }, "Payment Gateway Logs"),
    /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-secondary" }, "A list of all the logs in your account. Click a row for the full transaction timeline.")
  ])
], -1)
const _hoisted_3 = { class: "logs-filters flex flex-wrap items-center gap-2 pb-2" }
const _hoisted_4 = { class: "base-input" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("label", {
  class: "label-primary",
  for: "type"
}, "Type", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("option", { value: "" }, "All", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("option", { value: "init" }, "Setup", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("option", { value: "payment" }, "Payment", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("option", { value: "webhook" }, "Webhook", -1)
const _hoisted_10 = [
  _hoisted_6,
  _hoisted_7,
  _hoisted_8,
  _hoisted_9
]
const _hoisted_11 = { class: "base-input" }
const _hoisted_12 = /*#__PURE__*/_createElementVNode("label", {
  class: "label-primary",
  for: "status"
}, "Status", -1)
const _hoisted_13 = /*#__PURE__*/_createStaticVNode("<option value=\"\">All</option><option value=\"created\">Created</option><option value=\"pending\">Pending</option><option value=\"success\">Success</option><option value=\"failed\">Failed</option><option value=\"cancelled\">Cancelled</option><option value=\"error\">Error</option><option value=\"other\">Other</option>", 8)
const _hoisted_21 = [
  _hoisted_13
]
const _hoisted_22 = ["disabled"]
const _hoisted_23 = /*#__PURE__*/_createElementVNode("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 24 24",
  "stroke-width": "1.5",
  stroke: "currentColor",
  class: "w-4 h-4"
}, [
  /*#__PURE__*/_createElementVNode("path", {
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    d: "M6 18 18 6M6 6l12 12"
  })
], -1)
const _hoisted_24 = { class: "mt-2 bg-gray-100 p-4 rounded-md overflow-auto max-h-96 transition-all" }
const _hoisted_25 = { class: "text-sm" }

import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';

import { PaymentGatewayLogsI, PaginationI, TransactionReferenceI } from '@/interfaces/interfaces';

import BaseInput from '@/components/inputs/BaseInput.vue';
import OverlaySpinner from "@/components/loader/OverlaySpinner.vue";
import BaseModal from '@/components/common/BaseModal.vue';
import DateInput from '@/components/PaymentGatewayLogs/DateInput.vue';
import LogsTable from '@/components/PaymentGatewayLogs/LogsTable.vue';
import LogsPagination from '@/components/PaymentGatewayLogs/LogsPagination.vue';
import TransactionDetail from '@/components/PaymentGatewayLogs/TransactionDetail.vue';

interface SearchFiltersI {
  startDate: Date | null,
  endDate: Date | null,
  reference: string | null,
  type: string,
  status: string,
  amount: number | null,
  widgetName: string | null,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentGatewayLogs',
  setup(__props) {

const store = useStore();

const isLoading = ref(false);
const isExporting = ref(false);
const logs = ref<PaymentGatewayLogsI[]>([]);
const pagination = ref<PaginationI>({
  lastPage: 0,
  page: 1,
  perPage: 0,
  total: 0,
});
const emptyFilters = (): SearchFiltersI => ({
  startDate: null,
  endDate: null,
  reference: null,
  type: '',
  status: '',
  amount: null,
  widgetName: null,
});

const search = ref<SearchFiltersI>(emptyFilters());
const showPayloadModal = ref(false);
const currentPayload = ref('');
const showDetailModal = ref(false);
const detailReference = ref<TransactionReferenceI | null>(null);
const detailRows = ref<PaymentGatewayLogsI[]>([]);

onMounted(() => {
  getLogs();
})

// Local calendar date as Y-m-d — serialising the Date object itself would
// shift it through UTC and filter by the wrong day.
const toDateParam = (date: Date | null): string | null => {
  if (!date) {
    return null;
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const searchParams = () => ({
  startDate: toDateParam(search.value.startDate),
  endDate: toDateParam(search.value.endDate),
  reference: search.value.reference,
  type: search.value.type,
  status: search.value.status,
  amount: search.value.amount,
  widgetName: search.value.widgetName,
});

const openPayloadModal = (payload: string) => {
  try {
    const parsedPayload = JSON.parse(payload);
    currentPayload.value = JSON.stringify(parsedPayload, null, 2);
    showPayloadModal.value = true;
  } catch (error) {
    console.error('Error parsing payload:', error);
    // Fallback to raw string if JSON parsing fails
    currentPayload.value = payload;
    showPayloadModal.value = true;
  }
};

const closePayloadModal = () => {
  showPayloadModal.value = false;
  currentPayload.value = '';
};

// Show the clicked row alone when there is nothing to correlate on (or the
// timeline fetch fails) — a click must never silently do nothing.
const openSingleRowDetail = (log: PaymentGatewayLogsI) => {
  detailReference.value = null;
  detailRows.value = [log];
  showDetailModal.value = true;
};

const openDetail = (log: PaymentGatewayLogsI) => {
  // Rows like credential checks have no gateway transaction id or booking
  // reference — there is no wider lifecycle to fetch.
  if (!log.transactionReferenceId && !log.identifier) {
    openSingleRowDetail(log);
    return;
  }

  isLoading.value = true;

  store
    .dispatch('getPaymentGatewayLogTransaction', {
      transactionId: log.transactionReferenceId,
      reference: log.identifier,
    })
    .then((data) => {
      detailReference.value = data?.reference || null;
      detailRows.value = data?.data || [];
      showDetailModal.value = true;
      isLoading.value = false;
    })
    .catch((error) => {
      console.error(error);
      openSingleRowDetail(log);
      isLoading.value = false;
    });
};

const closeDetail = () => {
  showDetailModal.value = false;
  detailReference.value = null;
  detailRows.value = [];
};

const getLogs = async (isPagination = false) => {
  isLoading.value = true;

  store
    .dispatch('getPaymentGatewayLogs', {
      page: isPagination ? pagination.value.page : null,
      ...searchParams(),
    })
    .then(() => {
      logs.value = store.state.paymentGatewayLogs?.data || [];
      pagination.value = store.state.paymentGatewayLogs?.pagination || {};
      isLoading.value = false;
    })
    .catch((error) => {
      console.error(error);
      isLoading.value = false;
    })
}

const hasActiveFilters = computed(() =>
  Object.values(search.value).some(value => value !== null && value !== '')
);

const handleSearch = () => {
  getLogs();
}

const handleClear = () => {
  search.value = emptyFilters();
  getLogs();
}

const handleExport = () => {
  isExporting.value = true;

  store
    .dispatch('exportPaymentGatewayLogs', searchParams())
    .then(() => {
      isExporting.value = false;
    })
    .catch((error) => {
      console.error(error);
      isExporting.value = false;
    });
}

const handlePagination = (page: number) => {
  pagination.value.page = page;
  getLogs(true);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createVNode(DateInput, {
        id: "startDate",
        value: search.value.startDate,
        label: "From Date",
        class: "w-[190px]",
        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (search.value.startDate = $event))
      }, null, 8, ["value"]),
      _createVNode(DateInput, {
        id: "endDate",
        value: search.value.endDate,
        label: "To Date",
        class: "w-[190px]",
        "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => (search.value.endDate = $event))
      }, null, 8, ["value"]),
      _createVNode(BaseInput, {
        id: "reference",
        type: "text",
        value: search.value.reference,
        label: "Reference / Booking",
        placeholder: "Search",
        "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => (search.value.reference = $event))
      }, null, 8, ["value"]),
      _createElementVNode("div", _hoisted_4, [
        _hoisted_5,
        _withDirectives(_createElementVNode("select", {
          id: "type",
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((search.value.type) = $event)),
          class: "input-primary"
        }, _hoisted_10, 512), [
          [_vModelSelect, search.value.type]
        ])
      ]),
      _createElementVNode("div", _hoisted_11, [
        _hoisted_12,
        _withDirectives(_createElementVNode("select", {
          id: "status",
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((search.value.status) = $event)),
          class: "input-primary"
        }, _hoisted_21, 512), [
          [_vModelSelect, search.value.status]
        ])
      ]),
      _createVNode(BaseInput, {
        id: "amount",
        type: "number",
        value: search.value.amount,
        min: 0,
        label: "Amount",
        placeholder: "Search",
        "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => (search.value.amount = $event))
      }, null, 8, ["value"]),
      _createVNode(BaseInput, {
        id: "widgetName",
        type: "text",
        value: search.value.widgetName,
        label: "Widget Name",
        placeholder: "Search",
        "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => (search.value.widgetName = $event))
      }, null, 8, ["value"]),
      _createElementVNode("button", {
        type: "button",
        class: "btn-primary is-btn-size-default flex items-center gap-2",
        onClick: handleSearch
      }, " Search "),
      _createElementVNode("button", {
        type: "button",
        class: "btn-secondary is-btn-size-default flex items-center gap-2",
        disabled: isExporting.value,
        title: "Download the current filtered results as CSV",
        onClick: handleExport
      }, _toDisplayString(isExporting.value ? 'Exporting…' : 'Export CSV'), 9, _hoisted_22),
      (hasActiveFilters.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            class: "flex items-center gap-1 px-2 py-2 text-sm text-secondary hover:text-primary-main transition-colors",
            title: "Reset all filters",
            onClick: handleClear
          }, [
            _hoisted_23,
            _createTextVNode(" Clear filters ")
          ]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(LogsTable, {
      logs: logs.value,
      "onPayloadModal:open": openPayloadModal,
      "onDetail:open": openDetail
    }, null, 8, ["logs"]),
    _createVNode(LogsPagination, {
      pagination: pagination.value,
      "onUpdate:page": handlePagination
    }, null, 8, ["pagination"]),
    (showPayloadModal.value)
      ? (_openBlock(), _createBlock(BaseModal, {
          key: 0,
          title: "Response Payload",
          onCancel: closePayloadModal
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_24, [
              _createElementVNode("pre", _hoisted_25, _toDisplayString(currentPayload.value), 1)
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (showDetailModal.value)
      ? (_openBlock(), _createBlock(TransactionDetail, {
          key: 1,
          reference: detailReference.value,
          rows: detailRows.value,
          onClose: closeDetail
        }, null, 8, ["reference", "rows"]))
      : _createCommentVNode("", true),
    (isLoading.value)
      ? (_openBlock(), _createBlock(OverlaySpinner, { key: 2 }))
      : _createCommentVNode("", true)
  ]))
}
}

})